// frontend/src/pages/CRManagement/CRListPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Table, Button, Input, Typography, Space, Tag, Tooltip,
    Select, Popover, DatePicker, Badge
} from 'antd';
import {
    SearchOutlined, EyeOutlined, ReloadOutlined,
    FileTextOutlined, LinkOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import './CRListPage.css';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// ============================================================
// CR TYPE COLOR MAP (matches the reports)
// ============================================================
const CR_TYPE_COLORS = {
    1: 'green',     // Corrective Maintenance
    4: 'blue',      // Change Request
    7: 'gold',      // Preventive Maintenance
    3: 'default'    // NCM (Non-Corrective Maintenance)
};

const CR_TYPE_LABELS = {
    1: 'Corrective Maintenance',
    4: 'Change Request',
    7: 'Preventive Maintenance',
    3: 'NCM (Non-Corrective Maintenance)'
};

const DATE_FORMAT = 'DD/MM/YYYY';

// ============================================================
// COMPONENT
// ============================================================
const CRListPage = () => {
    const navigate = useNavigate();
    const notificationApi = useNotificationApi();
    const { user } = useAuth();

    const [loading, setLoading] = useState(false);
    const [crs, setCRs] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [contractsLoading, setContractsLoading] = useState(false);
    const [ticketTypes, setTicketTypes] = useState([]);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100']
    });

    // Filters
    const [search, setSearch] = useState('');
    const [filterContract, setFilterContract] = useState(undefined);
    const [filterTicketType, setFilterTicketType] = useState(undefined);
    const [filterDateRange, setFilterDateRange] = useState(null);

    // ------------------------------------------------------------
    // Notifications
    // ------------------------------------------------------------
    const showError = useCallback((title, content) => {
        notificationApi.error({
            message: title,
            description: content,
            duration: 4.5,
            placement: 'topRight',
            style: { width: 400 }
        });
    }, [notificationApi]);

    const showSuccess = useCallback((title, content) => {
        notificationApi.success({
            message: title,
            description: content,
            duration: 3,
            placement: 'topRight',
            style: { width: 400 }
        });
    }, [notificationApi]);

    // ------------------------------------------------------------
    // Load contracts + ticket types on mount
    // ------------------------------------------------------------
    useEffect(() => {
        const fetchContracts = async () => {
            setContractsLoading(true);
            try {
                const res = await api.get('/api/contracts');
                if (res.data.success) setContracts(res.data.data || []);
            } catch (err) {
                console.error('[CRListPage] Contracts fetch failed:', err);
            } finally {
                setContractsLoading(false);
            }
        };
        fetchContracts();
    }, []);

    useEffect(() => {
        const fetchTicketTypes = async () => {
            try {
                const res = await api.get('/api/ticket-types');
                const list = res.data?.data || res.data || [];
                setTicketTypes(list);
            } catch (err) {
                console.error('[CRListPage] Ticket types fetch failed:', err);
            }
        };
        fetchTicketTypes();
    }, []);

    // ------------------------------------------------------------
    // Fetch CRs
    // ------------------------------------------------------------
    const fetchCRs = useCallback(async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pageSize,
                sortBy: 'createdAt:DESC'
            };
            if (search) params.search = search;
            if (filterContract) params.contractId = filterContract;
            if (filterDateRange?.[0]) params.fromDate = filterDateRange[0].format('YYYY-MM-DD');
            if (filterDateRange?.[1]) params.toDate = filterDateRange[1].format('YYYY-MM-DD');

            const res = await api.get('/api/crs', { params });
            if (res.data.success) {
                setCRs(res.data.data || []);
                setPagination(prev => ({
                    ...prev,
                    current: res.data.pagination?.page || page,
                    pageSize: res.data.pagination?.limit || pageSize,
                    total: res.data.pagination?.total || 0
                }));
            }
        } catch (err) {
            console.error('[CRListPage] Fetch failed:', err);
            showError('Failed to Load', err.response?.data?.message || 'Could not load CR list.');
        } finally {
            setLoading(false);
        }
    }, [search, filterContract, filterDateRange, showError]);

    // ------------------------------------------------------------
    // Initial fetch + refetch on filter change
    // ------------------------------------------------------------
    useEffect(() => {
        fetchCRs(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, filterContract, filterTicketType, filterDateRange]);

    // ------------------------------------------------------------
    // Handlers
    // ------------------------------------------------------------
    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
        fetchCRs(newPagination.current, newPagination.pageSize);
    };

    const handleRefresh = () => {
        fetchCRs(pagination.current, pagination.pageSize);
        showSuccess('Refreshed', 'CR list refreshed.');
    };

    const handleClearFilters = () => {
        setSearch('');
        setFilterContract(undefined);
        setFilterTicketType(undefined);
        setFilterDateRange(null);
    };

    const hasActiveFilters = !!(
        search ||
        filterContract ||
        filterTicketType !== undefined ||
        filterDateRange
    );

    // ------------------------------------------------------------
    // Client-side filter by ticket type
    // (since backend doesn't support it yet on this endpoint)
    // ------------------------------------------------------------
    const displayedCRs = useMemo(() => {
        if (filterTicketType === undefined) return crs;
        return crs.filter(cr => cr.crTypeId === filterTicketType);
    }, [crs, filterTicketType]);

    // ------------------------------------------------------------
    // Columns
    // ------------------------------------------------------------
    const columns = useMemo(() => [
        {
            title: 'CR NUMBER',
            dataIndex: 'crNumber',
            key: 'crNumber',
            width: 200,
            fixed: 'left',
            render: (text, record) => (
                <a
                    onClick={(e) => {
                        e.preventDefault();
                        navigate(`/cr-management/${record.id}`);
                    }}
                    href={`/cr-management/${record.id}`}
                    style={{
                        fontWeight: 600,
                        color: '#4299e1',
                        fontSize: '13px',
                        textDecoration: 'none',
                        fontFamily: "'SF Mono', 'Monaco', 'Consolas', monospace"
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                >
                    {text || '—'}
                </a>
            )
        },
        {
            title: 'CONTRACT',
            dataIndex: 'contractName',
            key: 'contractName',
            width: 240,
            ellipsis: { showTitle: false },
            render: (text) => text ? (
                <Tooltip title={text} placement="topLeft">
                    <Tag color="blue" style={{ fontSize: '12px', fontWeight: 500 }}>
                        {text}
                    </Tag>
                </Tooltip>
            ) : <span style={{ color: '#a0aec0' }}>—</span>
        },
        {
            title: 'TITLE',
            dataIndex: 'title',
            key: 'title',
            ellipsis: { showTitle: false },
            width: 300,
            render: (text) => text ? (
                <Tooltip title={text} placement="topLeft">
                    <Text style={{ fontSize: '14px', fontWeight: 500 }}>{text}</Text>
                </Tooltip>
            ) : <Text type="secondary">—</Text>
        },
        {
            title: 'CR TYPE',
            dataIndex: 'crTypeId',
            key: 'crTypeId',
            width: 180,
            render: (typeId, record) => {
                if (!typeId) return <Tag color="default">UNCATEGORIZED</Tag>;
                const color = CR_TYPE_COLORS[typeId] || 'default';
                const label = record.crTypeName || CR_TYPE_LABELS[typeId] || 'Unknown';
                return <Tag color={color}>{label.toUpperCase()}</Tag>;
            }
        },
        {
            title: 'REQUEST DATE',
            dataIndex: 'requestDate',
            key: 'requestDate',
            width: 150,
            render: (date) => date ? (
                <div style={{ fontSize: '13px', color: '#4a5568', fontWeight: 500 }}>
                    {dayjs(date).format(DATE_FORMAT)}
                </div>
            ) : '—'
        },
        {
            title: 'LINKED TICKETS',
            dataIndex: 'linkedTicketCount',
            key: 'linkedTicketCount',
            width: 150,
            align: 'center',
            render: (count, record) => {
                const tag = (
                    <Tag
                        color={count > 0 ? 'blue' : 'default'}
                        icon={<LinkOutlined />}
                        style={{ cursor: count > 0 ? 'pointer' : 'default' }}
                    >
                        {count || 0}
                    </Tag>
                );
                if (!count || !record.linkedTickets?.length) return tag;

                const popoverContent = (
                    <div className="crl-ticket-popover">
                        <div className="crl-ticket-popover-title">
                            Linked Tickets ({record.linkedTickets.length})
                        </div>
                        {record.linkedTickets.map(t => (
                            <div key={t.id} className="crl-ticket-popover-item">
                                <div
                                    className="crl-ticket-uuid"
                                    onClick={() => navigate(`/tickets/${t.id}`)}
                                    title="Click to open ticket"
                                >
                                    {t.ticketUuid}
                                </div>
                                {t.title && (
                                    <div className="crl-ticket-title">{t.title}</div>
                                )}
                            </div>
                        ))}
                    </div>
                );

                return (
                    <Popover
                        content={popoverContent}
                        placement="top"
                        mouseEnterDelay={0.2}
                        mouseLeaveDelay={0.1}
                        overlayStyle={{ maxWidth: 420 }}
                    >
                        {tag}
                    </Popover>
                );
            }
        },
        {
            title: 'TIME ENTRIES',
            dataIndex: 'totalEntries',
            key: 'totalEntries',
            width: 130,
            align: 'center',
            render: (count) => (
                <Tag color={count > 0 ? 'cyan' : 'default'} icon={<ClockCircleOutlined />}>
                    {count || 0}
                </Tag>
            )
        },
        {
            title: 'TOTAL HOURS',
            dataIndex: 'totalHours',
            key: 'totalHours',
            width: 130,
            align: 'right',
            render: (val) => (
                <Text strong style={{ fontSize: '14px' }}>{parseFloat(val || 0).toFixed(2)}</Text>
            )
        },
        {
            title: 'MAN DAYS',
            dataIndex: 'totalManDays',
            key: 'totalManDays',
            width: 120,
            align: 'right',
            render: (val) => (
                <Text strong style={{ color: '#fa8c16', fontSize: '14px' }}>
                    {parseFloat(val || 0).toFixed(2)}
                </Text>
            )
        },
        {
            title: 'ACTIONS',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/cr-management/${record.id}`)}
                    style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        height: 'auto',
                        fontWeight: 600
                    }}
                >
                    View
                </Button>
            )
        }
    ], [navigate]);

    // ------------------------------------------------------------
    // Render
    // ------------------------------------------------------------
    return (
        <Card className="crl-list-page-card" bordered={false}>
            {/* Header */}
            <div className="crl-list-header">
                <Title level={2} className="crl-list-title">
                    CR Management
                </Title>
                <Text className="crl-list-description">
                    Browse, search and manage Change Requests across all contracts
                </Text>
            </div>

            {/* Filters */}
            <div className="crl-table-filters-container">
                <div className="crl-filters-left">
                    <Search
                        placeholder="Search CR number, title, or description..."
                        enterButton="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onSearch={setSearch}
                        style={{ width: 400 }}
                        className="crl-global-search"
                        size="large"
                        allowClear
                    />

                    <Select
                        placeholder="All Contracts"
                        value={filterContract}
                        onChange={setFilterContract}
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        size="large"
                        style={{ width: 240 }}
                        loading={contractsLoading}
                    >
                        {contracts.map(c => (
                            <Option key={c.id} value={c.id}>
                                {c.contractName || `Contract #${c.id}`}
                            </Option>
                        ))}
                    </Select>

                    <Select
                        placeholder="All CR Types"
                        value={filterTicketType}
                        onChange={setFilterTicketType}
                        allowClear
                        size="large"
                        style={{ width: 220 }}
                    >
                        {ticketTypes.map(tt => (
                            <Option key={tt.id} value={tt.id}>
                                {tt.name}
                            </Option>
                        ))}
                    </Select>

                    <RangePicker
                        value={filterDateRange}
                        onChange={setFilterDateRange}
                        format={DATE_FORMAT}
                        size="large"
                        placeholder={['From', 'To']}
                        style={{ width: 260 }}
                    />

                    {hasActiveFilters && (
                        <Button
                            type="link"
                            onClick={handleClearFilters}
                            size="large"
                        >
                            Clear Filters
                        </Button>
                    )}
                </div>

                <div className="crl-filters-right">
                    <div className="crl-stats-counter">
                        <Badge
                            count={displayedCRs.length}
                            showZero
                            style={{ backgroundColor: '#4299e1' }}
                        />
                        <span className="crl-stats-text">
                            {pagination.total} CR{pagination.total === 1 ? '' : 's'}
                        </span>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefresh}
                        size="large"
                    >
                        Refresh
                    </Button>
                    {!user?.roleName?.toLowerCase().includes('customer') && (
                        <Button
                            type="primary"
                            size="large"
                            icon={<FileTextOutlined />}
                            onClick={() => navigate('/cr-management/create')}
                        >
                            Create CR
                        </Button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="crl-table-container">
                <Table
                    columns={columns}
                    dataSource={displayedCRs}
                    rowKey="id"
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }}
                    className="crl-ant-table"
                    size="middle"
                    locale={{
                        emptyText: (
                            <div className="crl-empty">
                                <FileTextOutlined className="crl-empty-icon" />
                                <div className="crl-empty-title">No CRs found</div>
                                <div className="crl-empty-subtitle">
                                    {hasActiveFilters
                                        ? 'No CRs match your filters. Try adjusting or clearing them.'
                                        : 'Click "Create CR" to get started.'}
                                </div>
                            </div>
                        )
                    }}
                />
            </div>
        </Card>
    );
};

export default CRListPage;